import React, { useState } from 'react'
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Skeleton,
    Tooltip,
} from '@mui/material'

/**
 * Every action the audit log can hold (see server/db/schemas/admin-audit-log.js). Also drives the
 * filter dropdown, so an action missing here is one nobody can search for.
 */
const ACTION_LABELS: Record<string, string> = {
    // Cross-org super-admin panel.
    VIEW_ORGANIZATIONS: 'View orgs',
    VIEW_ORGANIZATION_DETAIL: 'View org',
    UPDATE_ORG_FLAGS: 'Update org flags',
    VIEW_ORG_USERS: 'View users',
    UPDATE_USER_ROLES: 'Update roles',
    VIEW_ORG_ROADMAPS: 'View roadmaps',
    VIEW_ROADMAP_ALLOWED_USERS: 'View roadmap access',
    UPDATE_ROADMAP_ACCESS: 'Update roadmap access',
    VIEW_ROADMAP_STATS: 'View roadmap stats',
    BULK_CREATE_USERS: 'Bulk create users',
    EXPORT_ORG_EVENTS: 'Export org events',
    // Org-admin panel, always scoped to the actor's own organization.
    ORG_ADMIN_CREATE_USER: 'Create user',
    ORG_ADMIN_UPDATE_USER: 'Update user',
    ORG_ADMIN_ACTIVATE_USER: 'Activate user',
    ORG_ADMIN_DEACTIVATE_USER: 'Deactivate user',
    ORG_ADMIN_UPDATE_ROADMAP: 'Update roadmap',
    ORG_ADMIN_ARCHIVE_ROADMAP: 'Archive roadmap',
    ORG_ADMIN_RESTORE_ROADMAP: 'Restore roadmap',
    ORG_ADMIN_UPDATE_ROADMAP_ACCESS: 'Grant / revoke access',
}

const ACTION_COLORS: Record<string, 'warning' | 'success' | 'info'> = {
    UPDATE_ORG_FLAGS: 'warning',
    UPDATE_USER_ROLES: 'warning',
    UPDATE_ROADMAP_ACCESS: 'warning',
    BULK_CREATE_USERS: 'success',
    ORG_ADMIN_CREATE_USER: 'success',
    ORG_ADMIN_ACTIVATE_USER: 'success',
    ORG_ADMIN_RESTORE_ROADMAP: 'success',
    ORG_ADMIN_DEACTIVATE_USER: 'warning',
    ORG_ADMIN_ARCHIVE_ROADMAP: 'warning',
    ORG_ADMIN_UPDATE_USER: 'info',
    ORG_ADMIN_UPDATE_ROADMAP: 'info',
    ORG_ADMIN_UPDATE_ROADMAP_ACCESS: 'info',
}

const actionLabel = (action: string) => ACTION_LABELS[action] || action

/** What the entry was about, in words: the record may since have been renamed or deleted. */
const targetLabel = (log: { metadata?: { targetLabel?: string } }) => log.metadata?.targetLabel ?? ''

type AuditValues = { previousValues?: Record<string, unknown>; newValues?: Record<string, unknown> }

const hasValues = (values?: Record<string, unknown>) => !!values && Object.keys(values).length > 0

const hasDetails = (log: AuditValues) => hasValues(log.previousValues) || hasValues(log.newValues)

/** Before beside after, since an entry records only the fields that actually changed. */
const formatDetails = (log: AuditValues) =>
    [
        hasValues(log.previousValues) && `Before:\n${JSON.stringify(log.previousValues, null, 2)}`,
        hasValues(log.newValues) && `After:\n${JSON.stringify(log.newValues, null, 2)}`,
    ]
        .filter(Boolean)
        .join('\n\n')

const AdminAuditLogsTab: React.FC = () => {
    const [page, setPage] = useState(1)
    const [actionFilter, setActionFilter] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const { useAdminAuditLogsQuery } = require('../../../shared/queries/admin')
    const { data, isLoading } = useAdminAuditLogsQuery({
        page,
        limit: 50,
        action: actionFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
    })

    const logs = data?.data?.logs || []
    const pagination = data?.data?.pagination

    const formatDate = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, { timeZoneName: 'short' })
    }

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label="Filter by Action"
                    value={actionFilter}
                    onChange={(e) => {
                        setActionFilter(e.target.value)
                        setPage(1)
                    }}
                    sx={{ minWidth: 180 }}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                >
                    <option value="">All Actions</option>
                    {Object.entries(ACTION_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>
                            {label}
                        </option>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                        setStartDate(e.target.value)
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                        setEndDate(e.target.value)
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                {[
                    { label: 'Last 7d', days: 7 },
                    { label: 'Last 30d', days: 30 },
                    { label: 'Last 90d', days: 90 },
                    { label: 'Last Year', days: 365 },
                ].map(({ label, days }) => (
                    <Chip
                        key={days}
                        label={label}
                        size="small"
                        variant={
                            startDate === new Date(Date.now() - days * 86400000).toISOString().slice(0, 10) &&
                            endDate === new Date().toISOString().slice(0, 10)
                                ? 'filled'
                                : 'outlined'
                        }
                        onClick={() => {
                            setStartDate(new Date(Date.now() - days * 86400000).toISOString().slice(0, 10))
                            setEndDate(new Date().toISOString().slice(0, 10))
                            setPage(1)
                        }}
                        sx={{ cursor: 'pointer' }}
                    />
                ))}
                <Button
                    size="small"
                    variant="outlined"
                    disabled={logs.length === 0}
                    onClick={() => {
                        const rows = logs.map((log: any) => ({
                            Timestamp: log.createdAt,
                            'Admin Name': log.adminUserId?.name ?? '',
                            'Admin Email': log.adminEmail ?? '',
                            Action: actionLabel(log.action),
                            Organization: log.targetOrganizationId?.name ?? '',
                            Target: targetLabel(log),
                            'Previous Values': log.previousValues ? JSON.stringify(log.previousValues) : '',
                            'New Values': log.newValues ? JSON.stringify(log.newValues) : '',
                        }))
                        const headers = Object.keys(rows[0])
                        const csv = [
                            headers.join(','),
                            ...rows.map((r: Record<string, string>) =>
                                headers.map((h) => JSON.stringify(r[h] ?? '')).join(',')
                            ),
                        ].join('\n')
                        const blob = new Blob([csv], { type: 'text/csv' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = 'audit-logs.csv'
                        a.click()
                        URL.revokeObjectURL(url)
                    }}
                >
                    Export CSV
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 320px)' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Time</TableCell>
                            <TableCell>Admin</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Organization</TableCell>
                            <TableCell>Target</TableCell>
                            <TableCell>Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading
                            ? Array.from({ length: 10 }).map((_, i) => (
                                  <TableRow key={i}>
                                      {Array.from({ length: 6 }).map((__, cell) => (
                                          <TableCell key={cell}>
                                              <Skeleton />
                                          </TableCell>
                                      ))}
                                  </TableRow>
                              ))
                            : logs.map((log: any) => (
                                  <TableRow key={log._id} hover>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                          <Typography variant="body2">{formatDate(log.createdAt)}</Typography>
                                      </TableCell>
                                      <TableCell>
                                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                              {log.adminUserId?.name || log.adminEmail}
                                          </Typography>
                                      </TableCell>
                                      <TableCell>
                                          <Chip
                                              label={actionLabel(log.action)}
                                              size="small"
                                              color={ACTION_COLORS[log.action] ?? 'default'}
                                              sx={{ height: 22, fontSize: 11 }}
                                          />
                                      </TableCell>
                                      <TableCell>
                                          <Typography variant="body2">
                                              {log.targetOrganizationId?.name || '—'}
                                          </Typography>
                                      </TableCell>
                                      <TableCell>
                                          <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                              {targetLabel(log) || '—'}
                                          </Typography>
                                      </TableCell>
                                      <TableCell>
                                          {hasDetails(log) && (
                                              <Tooltip
                                                  title={
                                                      <pre
                                                          style={{
                                                              margin: 0,
                                                              fontSize: 10,
                                                              maxWidth: 300,
                                                              overflow: 'auto',
                                                          }}
                                                      >
                                                          {formatDetails(log)}
                                                      </pre>
                                                  }
                                              >
                                                  <Chip
                                                      label="View"
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ height: 20, fontSize: 10, cursor: 'pointer' }}
                                                  />
                                              </Tooltip>
                                          )}
                                      </TableCell>
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {pagination && pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                    <Chip
                        label="← Prev"
                        size="small"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        sx={{ cursor: page === 1 ? 'default' : 'pointer' }}
                    />
                    <Chip label={`${page} / ${pagination.totalPages}`} size="small" variant="outlined" />
                    <Chip
                        label="Next →"
                        size="small"
                        onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                        disabled={page === pagination.totalPages}
                        sx={{ cursor: page === pagination.totalPages ? 'default' : 'pointer' }}
                    />
                </Box>
            )}
        </Box>
    )
}

export default AdminAuditLogsTab
